import axios from 'axios';
import {
  SearchResult,
  EloData,
  Competition,
  Club,
  EnhancedTournament,
  EloPreview,
  Game,
  SystemMessagesResponse
} from '../types/api.types';

// Use environment variable for API base URL, fallback to production
const API_BASE = process.env.REACT_APP_API_URL || 'https://apiv2.ttstats.ch';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'X-Client-Type': 'web',
    'X-Client-Version': '1.0.0'
  }
});

// Simple cache implementation
const cache = new Map<string, { data: any; timestamp: number }>();
const inFlight = new Map<string, Promise<any>>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

async function getCachedOrFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = cache.get(key);
  const now = Date.now();
  
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  const pending = inFlight.get(key) as Promise<T> | undefined;
  if (pending) return pending;

  const request = fetcher()
    .then((data) => {
      cache.set(key, { data, timestamp: Date.now() });
      return data;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

export const ttStatsAPI = {
  // Player endpoints
  searchPlayers: async (query: string): Promise<SearchResult> => {
    const response = await api.get(`/search/${encodeURIComponent(query)}`);
    // Combine all results from lastname, firstname, and matches
    const data = response.data;
    const allPlayers = [
      ...(data.lastname || []),
      ...(data.firstname || []),
      ...(data.matches || [])
    ];
    
    // Remove duplicates based on href
    const uniquePlayers = allPlayers.filter((player, index, self) =>
      index === self.findIndex((p) => p.href === player.href)
    );
    
    // Map to expected format
    return {
      players: uniquePlayers.map((p: any) => ({
        name: p.name,
        // Strip the club ID in parentheses (e.g., "Young Stars ZH (70026)" -> "Young Stars ZH")
        club: p.club ? p.club.replace(/\s*\(\d+\)$/, '') : undefined,
        elo: p.elo ? parseInt(p.elo) : undefined,
        url: p.href,
        isPro: p.isPro === true
      }))
    };
  },

  getPlayerDetails: async (url: string): Promise<any> => {
    return getCachedOrFetch(`player_${url}`, async () => {
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
      const response = await api.get('/player', { params: { url: cleanUrl } });
      return response.data;
    });
  },

  getPlayerElo: async (url: string, playerName?: string, playerClub?: string): Promise<EloData> => {
    return getCachedOrFetch(`player_${url}`, async () => {
      // Remove the leading slash if present
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url;

      // Use the /player endpoint which returns EVERYTHING in one request
      const response = await api.get('/player', {
        params: { url: cleanUrl },
        // Fail faster so stale/invalid links transition to 404 quickly.
        timeout: 6000
      });
      const data = response.data;

      // Extract player name from the API response
      let playerNameValue = data.name || 'Unknown Player';

      // Clean the club name (remove ID in parentheses)
      // Note: The actual clubId comes from data.clubId, NOT from the parentheses in the club name
      let clubValue = '';
      if (data.club) {
        clubValue = data.club.replace(/\s*\(\d+\)$/, '');
      }
      // Use the clubId field directly from the API response
      const clubId = data.clubId ? String(data.clubId) : '';

      // Transform the data to match our component expectations
      const transformedData: EloData = {
        ...data,
        player_name: playerNameValue,
        club: clubValue,
        clubId: clubId,
        current_elo: data.official_elo || data.final_elo,
        classification: data.classification || data.ranking_info?.current_level || '',
        // Use playerHref as the stable URL (it never expires)
        stable_url: data.playerHref,
        // Transform elo_records to elo_history format
        elo_history: data.elo_records?.dates?.map((date: string, index: number) => ({
          date,
          elo: data.elo_records.elo_points[index]
        })) || [],
        // Transform games to include expected fields
        games: (data.games || []).map((game: any) => ({
          ...game,
          opponent: `${game.enemy_first_name} ${game.enemy_last_name}`,
          opponent_elo: game.enemy_elo,
          elo_change: game.elo_delta,
          player_elo: game.month_start_elo,
          sets_won: game.win ? 3 : 0,
          sets_lost: game.win ? 0 : 3,
          competition: game.counterparties
        }))
      };

      // If we have stats from the API, merge them with expected format
      if (data.stats) {
        transformedData.stats = {
          total_games: data.games?.length || 0,
          wins: data.games?.filter((g: any) => g.win).length || 0,
          losses: data.games?.filter((g: any) => !g.win).length || 0,
          win_percentage: data.games?.length > 0
            ? (data.games.filter((g: any) => g.win).length / data.games.length) * 100
            : 0,
          total_sets_won: data.games?.filter((g: any) => g.win).length * 3 || 0,
          total_sets_lost: data.games?.filter((g: any) => !g.win).length * 3 || 0,
          set_ratio: 1,
          best_win: data.stats.best_win ? {
            opponent: data.stats.best_win.opponent,
            opponent_elo: data.stats.best_win.enemy_elo,
            date: data.stats.best_win.date
          } : undefined,
          highest_elo: Math.max(...(data.elo_records?.elo_points || [data.official_elo])),
          lowest_elo: Math.min(...(data.elo_records?.elo_points || [data.official_elo])),
          elo_change_30d: data.stats.total_elo_change || 0,
          // Add the vs_higher, vs_lower, and best_streak data
          vs_higher: data.stats.vs_higher,
          vs_lower: data.stats.vs_lower,
          best_streak: data.stats.best_streak
        };
      }

      return transformedData;
    });
  },

  getPlayerCompetitions: async (url: string): Promise<Competition[]> => {
    return getCachedOrFetch(`competitions_${url}`, async () => {
      // Return empty array for now since the endpoint doesn't exist
      // This will be handled properly when competitions are available from the ELO endpoint
      return [];
    });
  },

  getPlayerHistory: async (url: string): Promise<Game[]> => {
    return getCachedOrFetch(`history_${url}`, async () => {
      // Use games from the ELO endpoint since history endpoint doesn't exist
      // The ELO endpoint already returns games data
      return [];
    });
  },

  // Club endpoints
  searchClubs: async (query: string): Promise<{ clubs: Club[] }> => {
    const response = await api.get('/clubsearch', { params: { searchFor: query } });
    return response.data;
  },

  // League endpoints
  getLeagueInfo: async (url: string): Promise<any> => {
    return getCachedOrFetch(`league_${url}`, async () => {
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
      const response = await api.get('/league', { params: { url: cleanUrl } });
      return response.data;
    });
  },

  // Alias for backwards compatibility
  getLeagueStandings: async (url: string): Promise<any> => {
    return ttStatsAPI.getLeagueInfo(url);
  },

  // Game endpoints
  getGameDetails: async (url: string): Promise<any> => {
    return getCachedOrFetch(`game_${url}`, async () => {
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
      const response = await api.get('/game', { params: { url: cleanUrl } });
      return response.data;
    });
  },

  // Swiss TT Tournaments endpoint
  getTournaments: async (
    federation: string = 'STT',
    queryDateFrom: string = '01.01.2025',
    queryDateTo: string = '30.06.2029',
    preferredLanguage: string = 'German'
  ): Promise<{ tournaments: any[] }> => {
    return getCachedOrFetch(`tournaments_${federation}_${queryDateFrom}_${queryDateTo}`, async () => {
      const response = await api.get('/tournaments', {
        params: { federation, queryDateFrom, queryDateTo, preferredLanguage }
      });
      return response.data;
    });
  },

  // Tournament details (not supported by API v2 - placeholder for backwards compatibility)
  getTournamentDetails: async (url: string): Promise<any> => {
    // This endpoint doesn't exist in API v2
    return null;
  },

  // ELO Preview (calculated client-side)
  getEloPreview: (playerElo: number, opponentElo: number): EloPreview => {
    const K = 15; // K-factor for Swiss table tennis
    const ratingDiff = opponentElo - playerElo;
    const expectedScore = 1 / (1 + Math.pow(10, ratingDiff / 200));
    const eloChangeWin = Math.round(K * (1 - expectedScore) * 10) / 10;
    const eloChangeLoss = Math.round(K * (0 - expectedScore) * 10) / 10;

    return {
      player_elo: playerElo,
      opponent_elo: opponentElo,
      win_probability: expectedScore,
      elo_change_win: eloChangeWin,
      elo_change_loss: eloChangeLoss,
      elo_if_win: Math.round((playerElo + eloChangeWin) * 10) / 10,
      elo_if_loss: Math.round((playerElo + eloChangeLoss) * 10) / 10,
    };
  },

  // Clear cache
  clearCache: () => {
    cache.clear();
  },

  // Monthly Rankings
  getMonthlyRankings: async () => {
    return getCachedOrFetch('monthlyRankings', async () => {
      const response = await api.get('/monthlyRankings');
      return response.data;
    });
  },

  // Club endpoints
  getClubInfo: async (clubId: string) => {
    return getCachedOrFetch(`club_${clubId}`, async () => {
      const response = await api.get(`/club/${clubId}`);
      return response.data;
    });
  },

  getClubTeams: async (clubId: string) => {
    return getCachedOrFetch(`clubTeams_${clubId}`, async () => {
      const response = await api.get(`/club-teams/${clubId}`);
      return response.data;
    });
  },

  getClubPlayers: async (clubId: string) => {
    return getCachedOrFetch(`clubPlayers_${clubId}`, async () => {
      const response = await api.get(`/clubplayers/${clubId}`);
      return response.data;
    });
  },

  // Tournament API (tt-turnier.ch)
  getUpcomingTournaments: async (limit: number = 10, filterOpen: boolean = false): Promise<EnhancedTournament[]> => {
    const cacheKey = `upcomingTournaments_${limit}_${filterOpen}`;
    return getCachedOrFetch(cacheKey, async () => {
      try {
        // Fetch all tournaments (basic data)
        const response = await axios.get(
          'https://tt-turnier.ch/en/wp-json/tramp/v1/data',
          {
            params: {
              type: 'Tournament',
              all_sites: '1',
              blog_id: ''
            },
            timeout: 90000
          }
        );

        const tournaments: EnhancedTournament[] = response.data;

        // Filter visible tournaments
        const visible = tournaments.filter(t =>
          t.options.show_in_archive &&
          !t.options.hide_in_global_archive &&
          !t.options.test_tournament
        );

        // Filter upcoming tournaments
        const now = Date.now();
        const upcoming = visible
          .filter(t => {
            const startTime = t.options.planned_start_time;
            return startTime && startTime > now;
          })
          .sort((a, b) => {
            const dateA = a.options.planned_start_time || 0;
            const dateB = b.options.planned_start_time || 0;
            return dateA - dateB;
          });

        // Enhance tournaments in parallel batches and stop when we have enough
        const enhanced: EnhancedTournament[] = [];
        const batchSize = 5; // Parallel requests per batch

        for (let i = 0; i < upcoming.length; i += batchSize) {
          // Stop if we already have enough matching tournaments
          if (enhanced.length >= limit) break;

          const batch = upcoming.slice(i, i + batchSize);
          const batchPromises = batch.map(tournament =>
            axios.get(
              'https://tt-turnier.ch/en/wp-json/tramp/v1/tournament',
              {
                params: {
                  tournament: tournament.id,
                  blog_id: tournament.blog_id,
                  additional_data: 'true'
                },
                timeout: 90000
              }
            )
            .then(response => response.data)
            .catch(error => {
              console.error(`Failed to enhance tournament ${tournament.id}:`, error);
              return tournament; // Fallback to basic data
            })
          );

          const batchResults = await Promise.all(batchPromises);

          // Filter results if needed
          for (const result of batchResults) {
            if (enhanced.length >= limit) break;

            // If filtering for open registration, check registration status
            if (filterOpen) {
              const regEndTime = result.options.reg_end_time;
              if (!regEndTime || result.options.block_enrollment) continue;

              const regEndTimestamp = parseInt(regEndTime);
              if (isNaN(regEndTimestamp) || now >= regEndTimestamp) continue;
            }

            enhanced.push(result);
          }
        }

        return enhanced.slice(0, limit);
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        return [];
      }
    });
  },

  // System Messages (web app doesn't use version filtering)
  getSystemMessages: async (): Promise<SystemMessagesResponse> => {
    return getCachedOrFetch('systemMessages', async () => {
      const response = await api.get('/messages');
      return response.data;
    });
  }
};
